/* eslint-disable @next/next/no-img-element */
import {
  DeliveryType,
  GetDeliveriesDocument,
  useDeleteDeliveryMutation,
  useUpdateDeliveryMutation,
} from "@/graphql/generated/schema";
import { CheckCircle, CircleX, Trash } from "lucide-react";
import Button from "@/designs/Button";
import Input from "@/designs/Input";
import { twMerge } from "tailwind-merge";
import { CMSModal } from "@/context";
import { useContext, useEffect, useState } from "react";
import moment from "moment";
import Loader from "@/designs/Loader";
import { Divider } from "antd";
import { useTranslation } from "react-i18next";

export interface RecipientDeliveryProp {
  delivery: DeliveryType | null;
  onClose: () => void;
}
const RecipientDelivery = ({ delivery, onClose }: RecipientDeliveryProp) => {
  const { theme } = useContext(CMSModal);
  const { t } = useTranslation();
  const [note, setNote] = useState(delivery?.note ?? "");

  useEffect(() => {
    setNote(delivery?.note ?? "");
  }, [delivery?.id]);

  const isNoteDirty = note !== (delivery?.note ?? "");

  const [updateDelivery, { loading }] = useUpdateDeliveryMutation({
    refetchQueries: [GetDeliveriesDocument],
  });

  const [deleteDelivery, { loading: deleteLoading }] =
    useDeleteDeliveryMutation({
      refetchQueries: [GetDeliveriesDocument],
    });

  const collectDelivery = async () => {
    await updateDelivery({
      variables: {
        input: {
          _id: delivery?.id,
          collected: true,
          location: delivery?.location?.id||'',
        },
      },
    });
    onClose();
  };
  const notifyRecipient = async () => {
    await updateDelivery({
      variables: {
        input: {
          _id: delivery?.id,
          notify: true,
          location: delivery?.location?.id||'',
        },
      },
    });
    onClose();
  };

  const saveNote = async () => {
    await updateDelivery({
      variables: {
        input: { _id: delivery?.id, note, location: delivery?.location?.id as string },
      },
    });
  };

  const deleteData = async () => {
    await deleteDelivery({
      variables: {
        id: delivery?.id,
      },
    });
    onClose();
  };

  return (
    <div className="space-y-6">
      {(loading || deleteLoading) && <Loader />}
      <div className="flex items-center gap-2">
        <img
          src={delivery?.reciepient?.img || "/Avatar.svg"}
          alt="Visitor"
          className="w-20 h-20 rounded-full object-cover"
        />

        <div>
          <h2
            className={twMerge(
              "text-lg font-bold transition-colors",
              theme === "dark" ? "text-gray-100" : "",
            )}
          >
            {`${delivery?.reciepient?.firstName || ""} ${delivery?.reciepient?.lastName || ""}` ||
              t("deliveries.viewDelivery.unknownRecipient")}
          </h2>
        </div>
      </div>
      <Divider />
      <Input
        label={t("deliveries.viewDelivery.dateDelivered")}
        value={moment(delivery?.delivered).format("MMMM Do,h:mma") || ""}
        readonly
      />
      {
        <div className="space-y-2">
          {delivery?.signature ? (
            <span
              className={twMerge(
                "flex items-center gap-2",
                theme === "dark" ? "text-gray-300" : "",
              )}
            >
              <CheckCircle color="green" />
              {t("deliveries.viewDelivery.signatureRequired")}
            </span>
          ) : (
            <span
              className={twMerge(
                "flex items-center gap-2",
                theme === "dark" ? "text-gray-300" : "",
              )}
            >
              <CircleX color="gray" />
              {t("deliveries.viewDelivery.signatureNotRequired")}
            </span>
          )}
          <div
            className={twMerge(
              "flex items-center gap-2",
              theme === "dark" ? "text-gray-300" : "",
            )}
          >
            {" "}
            <CheckCircle color="green" />
            {t("deliveries.viewDelivery.notificationSent")}{" "}
            {!delivery?.collected && (
              <span
                onClick={notifyRecipient}
                className="cursor-pointer text-[#099ed1]"
              >
                {t("deliveries.viewDelivery.resend")}
              </span>
            )}
          </div>
          {delivery?.collected && (
            <div
              className={twMerge(
                "flex items-center gap-2",
                theme === "dark" ? "text-gray-300" : "",
              )}
            >
              {" "}
              <CheckCircle color="green" />
              {t("deliveries.viewDelivery.collectedAt", {
                date: moment(delivery?.collectedAt).format("h:mma, MMM Do"),
              })}
            </div>
          )}
        </div>
      }
      <div className="space-y-1">
        <label
          className={twMerge(
            "block text-sm font-medium transition-colors",
            theme === "dark" ? "text-gray-300" : "text-gray-700",
          )}
        >
          {t("deliveries.viewDelivery.note")}
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("deliveries.viewDelivery.notePlaceholder")}
          rows={3}
          className={twMerge(
            "px-3 py-2 border rounded-md w-full transition-colors focus:outline-none focus:ring-1 focus:ring-[#099ed1] resize-none text-sm",
            theme === "dark"
              ? "bg-[#191919] border-gray-700 text-gray-100 placeholder-gray-600"
              : "bg-white border-gray-300",
          )}
        />
        {isNoteDirty && (
          <div className="flex justify-end">
            <Button className="w-32" onClick={saveNote} loading={loading}>
              {t("deliveries.viewDelivery.saveNote")}
            </Button>
          </div>
        )}
      </div>
      {!delivery?.collected && (
        <>
          <Divider />
          <div className="flex items-center justify-between">
            <span
              className="flex items-center gap-2 text-red-400 cursor-pointer"
              onClick={deleteData}
            >
              <Trash size={18} /> {t("deliveries.viewDelivery.deleteButton")}
            </span>

            <Button className="w-40" onClick={collectDelivery}>
              {t("deliveries.viewDelivery.markCollected")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default RecipientDelivery;
